const initializedLaunches = new WeakSet();

const revealGroups = [
  ['.fu-launch__copy', 'hero'],
  ['.fu-launch__card-picker--intro', 'card'],
  ['.fu-launch__media', 'hero-media'],
  ['.fu-launch__conversion', 'hero'],
  ['.fu-launch__inside-heading', 'section'],
  ['.fu-launch__gallery figure', 'card'],
  ['.fu-launch__premium-list li', 'card'],
  ['.fu-launch__preorder > div', 'section'],
  ['.fu-launch__preorder li', 'card'],
  ['.fu-launch__faq .fu-launch__section-heading', 'section'],
  ['.fu-launch__faq details', 'card'],
  ['.fu-launch__final', 'section'],
];

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function loadInstagramEmbeds() {
  const instagramEmbedScript = 'https://www.instagram.com/embed.js';

  const processEmbeds = () => {
    if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process();
    }
  };

  if (document.querySelector(`script[src="${instagramEmbedScript}"]`)) {
    processEmbeds();
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = instagramEmbedScript;
  script.addEventListener('load', processEmbeds);
  document.head.appendChild(script);
}

function removeRetiredSocialLinks(root = document) {
  const instagramPosts = [
    {
      label: 'Prompt',
      title: 'Conversation starters',
      text: 'Faith-centred questions and reflections from the Faith Unfiltered page.',
      url: 'https://www.instagram.com/p/DZziQ2HjmgF/?hl=en',
    },
    {
      label: 'Reels',
      title: 'Honest faith moments',
      text: 'Short posts and reels made to invite deeper connection.',
      url: 'https://www.instagram.com/p/DZ948wnufzu/?hl=en',
    },
    {
      label: 'Product news',
      title: 'Product updates',
      text: 'Follow the Faith Unfiltered page for product news and updates.',
      url: 'https://www.instagram.com/p/DadKJQrAXiT/?hl=en',
    },
  ];

  root.querySelectorAll('.fu-launch__instagram-grid').forEach((grid) => {
    const currentPermalinks = [...grid.querySelectorAll('.instagram-media')].map((embed) =>
      embed.getAttribute('data-instgrm-permalink')
    );
    const hasRequestedPosts = instagramPosts.every((post) => currentPermalinks.includes(post.url));

    if (hasRequestedPosts) return;

    grid.replaceChildren(
      ...instagramPosts.map((post) => {
        const card = document.createElement('article');
        card.className = 'fu-launch__instagram-card';

        const copy = document.createElement('div');
        copy.className = 'fu-launch__instagram-card-copy';

        const label = document.createElement('span');
        label.textContent = post.label;

        const title = document.createElement('strong');
        title.textContent = post.title;

        const text = document.createElement('p');
        text.textContent = post.text;

        const embed = document.createElement('blockquote');
        embed.className = 'instagram-media';
        embed.dataset.instgrmCaptioned = '';
        embed.dataset.instgrmPermalink = post.url;
        embed.dataset.instgrmVersion = '14';

        const link = document.createElement('a');
        link.href = post.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = post.title;

        copy.append(label, title, text);
        embed.append(link);
        card.append(copy, embed);
        return card;
      })
    );
  });

  if (root.querySelector('.fu-launch .instagram-media')) {
    loadInstagramEmbeds();
  }
}

function markRevealElements(launch) {
  revealGroups.forEach(([selector, type]) => {
    launch.querySelectorAll(selector).forEach((element, index) => {
      element.dataset.fuReveal = type;
      element.style.setProperty('--fu-reveal-index', index);
      element.style.setProperty('--fu-reveal-delay', `${index * 80}ms`);
    });
  });
}

function revealOnScroll(launch) {
  const revealElements = [...launch.querySelectorAll('[data-fu-reveal]')];

  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    revealElements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const scrollRoot = document.querySelector('.page-wrapper');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      root: scrollRoot || null,
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.16,
    }
  );

  revealElements.forEach((element) => observer.observe(element));
}

function moveProductImage(launch) {
  const media = launch.querySelector('.fu-launch__media');

  if (!media || prefersReducedMotion() || window.matchMedia('(max-width: 700px)').matches) return;

  media.dataset.fuParallax = '';

  const scrollRoot = document.querySelector('.page-wrapper');
  let frame = null;

  const update = () => {
    frame = null;

    const rect = media.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height) - 0.5;
    const clampedProgress = Math.max(-0.65, Math.min(0.65, progress));
    const drift = Math.round(clampedProgress * 34);

    media.style.setProperty('--fu-image-drift', `${drift}px`);
  };

  const requestUpdate = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('resize', requestUpdate, { passive: true });
  window.addEventListener('scroll', requestUpdate, { passive: true });
  scrollRoot?.addEventListener('scroll', requestUpdate, { passive: true });
}

function initHomepageStickyCart(launch) {
  const stickyCart = launch.querySelector('[data-fu-sticky-cart]');
  const conversion = launch.querySelector('.fu-launch__conversion');
  const primaryButton = conversion?.querySelector('.fu-launch__form button[type="submit"]');

  if (!stickyCart || !conversion || !primaryButton) return;

  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  const scrollRoot = document.querySelector('.page-wrapper');
  let frame = null;

  const update = () => {
    frame = null;
    const conversionPassed = conversion.getBoundingClientRect().bottom < 0;
    const footerIsNear = footer ? footer.getBoundingClientRect().top < window.innerHeight + 160 : false;
    const visible = conversionPassed && !footerIsNear;
    stickyCart.classList.toggle('is-visible', visible);
    stickyCart.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const requestUpdate = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  scrollRoot?.addEventListener('scroll', requestUpdate, { passive: true });

  stickyCart.querySelector('[data-fu-sticky-add]')?.addEventListener('click', () => primaryButton.click());
}

function initFaithUnfilteredMotion(root = document) {
  removeRetiredSocialLinks(root);

  root.querySelectorAll('[data-fu-motion]').forEach((launch) => {
    if (initializedLaunches.has(launch)) return;

    initializedLaunches.add(launch);
    markRevealElements(launch);
    window.requestAnimationFrame(() => {
      launch.classList.add('fu-motion-ready');
      revealOnScroll(launch);
      moveProductImage(launch);
      initHomepageStickyCart(launch);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFaithUnfilteredMotion(), { once: true });
} else {
  initFaithUnfilteredMotion();
}

document.addEventListener('shopify:section:load', (event) => initFaithUnfilteredMotion(event.target));
